import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useLastMinuteFeed } from "../../hooks/last-minute/useLastMinuteFeed";
import { useSentRequestsMap } from "../../hooks/last-minute/useOpportunityRequests";
import { useDebounce } from "../../hooks/useDebounce";
import { lastMinuteService } from "../../services/lastMinuteService";
import { searchService } from "../../services/api";
import ListingCard from "../../components/last-minute/ListingCard";
import ListingComposer from "../../components/last-minute/ListingComposer";
import OpportunityComposer from "../../components/last-minute/OpportunityComposer";
import LmQuickViewModal from "../../components/last-minute/LmQuickViewModal";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import LmFilterSheet from "../../components/last-minute/LmFilterSheet";
import { MagnifierGlyph, PendingClockGlyph, EmptyClockGlyph, ensureLmShimmer } from "../../components/last-minute/lmGlyphs";
import {
  POSITION_GROUPS,
  groupForPosition,
  codeForPosition,
  MV_PRESETS,
  SALARY_PRESETS,
  parseMoneyShorthand,
  bandChipLabel,
} from "../../components/last-minute/lmFilterMeta";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { URGENCY } from "../../utils/lastMinute";
import { ROLES } from "../../config/roles";

// =====================================================================
// Last Minute browse — V2 UI rebuild 1:1 from the design handoff
// (artboards 1a/1b/1c/1d/1e club view, 3a agent-colleague view, 9e filter
// details, 9f quick view, 8a/8b mobile). Presentation layer only: feed
// gates, reveal flow, blocked-contact rules and the message-request
// pipeline are untouched (state + services identical).
//
// Artboard grammar across the seven widths (390/768/1024/1280/1440/1920/
// 2560): breadcrumb · title + info dot (+ agent "+ List a player") ·
// Professional/Youth segmented control | divider | Players/Staff UNDERLINE
// tabs · right-aligned 250px search (mobile: its own row, V2 grammar) ·
// urgency-class pill row (dark "All" + dotted white pills; club right slot
// = Sort, agent right slot = "Showing listings visible to agents") ·
// filter-chip row with dropdown panels per 9e + switch toggles + count/
// "Clear all" · card grid 1/2/2/3/3/4/5 columns capped 1800px @1920 and
// 2160px @2560 (Agent-Request-V2 ladder; the 1440 artboard's 3 columns sit
// on it exactly) · 1e shimmer skeletons · 1d empty state.
// =====================================================================

const CLUB_ROLES = [ROLES.ClubAdmin, ROLES.Staff, ROLES.Coach];

const URGENCY_TABS = [
  { value: "", label: "All", dot: null },
  { value: "contract_free", label: "Contract-free", dot: URGENCY.contract_free.dot },
  { value: "expiring_precontract", label: "Expiring & pre-contract", dot: URGENCY.expiring_precontract.dot },
  { value: "for_sale", label: "For sale", dot: URGENCY.for_sale.dot },
];

// Client-side presentation sort (1a "Sort · Newest"): pure reorderings of the
// already-fetched rows — no API change.
const SORT_OPTIONS = [
  { key: "newest", label: "Newest" },
  { key: "ending", label: "Ending soon" },
  { key: "oldest", label: "Oldest" },
];
const sortRows = (rows, key) => {
  const ts = (v) => (v ? new Date(v).getTime() : 0);
  const arr = [...rows];
  if (key === "ending") arr.sort((a, b) => ts(a.expires_at) - ts(b.expires_at));
  else if (key === "oldest") arr.sort((a, b) => ts(a.created_at) - ts(b.created_at));
  else arr.sort((a, b) => ts(b.created_at) - ts(a.created_at));
  return arr;
};

// 1e shimmer card skeleton (artboard anatomy: avatar + two lines + pill · pill
// row · text line · divider · agent line · two footer buttons).
const CardSkeleton = () => (
  <div className="bg-white rounded-2xl p-[18px] border border-[#E4E9F1] flex flex-col gap-3.5">
    <div className="flex items-start gap-3">
      <span className="lm-shim w-[52px] h-[52px] rounded-full flex-none" />
      <div className="flex-1 flex flex-col gap-2 pt-[5px]">
        <span className="lm-shim h-[13px] w-[62%] rounded-md" />
        <span className="lm-shim h-[10px] w-[42%] rounded-md" />
      </div>
      <span className="lm-shim w-[84px] h-6 rounded-full" />
    </div>
    <div className="flex gap-2">
      <span className="lm-shim w-[92px] h-6 rounded-full" />
      <span className="lm-shim w-[110px] h-6 rounded-full" />
    </div>
    <span className="lm-shim h-[10px] w-[88%] rounded-md" />
    <div className="h-px bg-[#EEF1F6] mt-auto" />
    <div className="flex items-center gap-2">
      <span className="lm-shim w-6 h-6 rounded-full" />
      <span className="lm-shim h-[10px] w-[45%] rounded-md" />
    </div>
    <div className="flex gap-2">
      <span className="lm-shim flex-[1.3] h-9 rounded-[10px]" />
      <span className="lm-shim flex-1 h-9 rounded-[10px]" />
    </div>
  </div>
);

// Filter chip + dropdown panel shell (design 1a chips / 1b active tokens).
// Closed inactive: white rounded-10 chip with a ▼. Active: blue token with the
// value + ✕ (clears). The open panel is the 9e-styled dropdown content.
const FilterChip = ({ label, activeLabel, onClear, children, panelWidth = 300 }) => {
  const [open, setOpen] = useState(false);
  const active = !!activeLabel;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-[7px] rounded-[10px] px-[13px] py-2 text-[12.5px] font-semibold whitespace-nowrap ${
          active
            ? "bg-[#E9F2FF] border border-[#BDD9FF] text-[#1466D8]"
            : "bg-white border border-[#DFE6EF] text-[#33475E]"
        }`}
      >
        {active ? activeLabel : label}
        {active ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${label}`}
            className="text-[11px] text-[#6FA5EA] leading-none"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onClear?.();
            }}
          >
            ✕
          </span>
        ) : (
          <span className="text-[9px] text-[#8A98AC] leading-none" aria-hidden="true">▼</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 z-20 mt-1.5 bg-white border border-[#D8DFE9] rounded-xl shadow-[0_16px_40px_-16px_rgba(16,24,40,0.3)] p-3.5 max-h-[min(420px,70vh)] overflow-y-auto"
            style={{ width: panelWidth, maxWidth: "calc(100vw - 32px)" }}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};

const pillCls = (active, extra = "") =>
  `rounded-full font-semibold whitespace-nowrap ${extra} ${
    active
      ? "border-[1.5px] border-[#2283FF] bg-[#F0F7FF] text-[#1466D8] font-bold"
      : "border border-[#DFE6EF] bg-white text-[#4A5C72]"
  }`;

// Artboard switch (34×20 track, 16px knob).
const Switch = ({ on, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onToggle}
    className={`relative inline-block w-[34px] h-5 rounded-full flex-none transition-colors ${on ? "bg-[#2283FF]" : "bg-[#D5DDE8]"}`}
  >
    <span
      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,.25)] transition-all ${
        on ? "right-0.5" : "left-0.5"
      }`}
    />
  </button>
);

const LastMinutePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentRole = useSelector((s) => s.auth.currentRole);
  const user = useSelector((s) => s.auth.user);
  const isClub = CLUB_ROLES.includes(currentRole);
  const isNarrow = useMediaQuery("(max-width: 640px)");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Round 2: the level control is the binary Professional/Youth segmented
  // toggle (1a) — professional is the default lens.
  const [level, setLevel] = useState("professional");
  const [listingType, setListingType] = useState("player");
  const [urgency, setUrgency] = useState("");
  const [readyToFly, setReadyToFly] = useState(false);
  const [shortTerm, setShortTerm] = useState(false);
  const [salaryMax, setSalaryMax] = useState(null);
  const [salaryCustom, setSalaryCustom] = useState({ min: "", max: "" });
  const [mvMin, setMvMin] = useState(""); // absolute euros (presets 9e) or ""
  const [mvMax, setMvMax] = useState("");
  const [mvCustom, setMvCustom] = useState({ min: "", max: "" });
  const [country, setCountry] = useState(""); // country NAME string → BE ?country=
  const [countries, setCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState("");
  const [position, setPosition] = useState("");
  const [posGroup, setPosGroup] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [sort, setSort] = useState("newest");
  const [sortOpen, setSortOpen] = useState(false);
  const [searchFocus, setSearchFocus] = useState(false);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 350);

  useEffect(() => {
    ensureLmShimmer();
    let active = true;
    searchService.getCountries("").then((r) => active && r.success && setCountries(r.data || [])).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const filters = useMemo(
    () => ({
      category: level,
      listing_type: listingType,
      ready_to_fly: readyToFly,
      short_term: shortTerm,
      salary_max: salaryMax || "",
      mv_min: mvMin || "",
      mv_max: mvMax || "",
      country: country || "",
      position: position || "",
      age_min: ageMin || "",
      age_max: ageMax || "",
      q: debouncedQ.trim(),
    }),
    [level, listingType, readyToFly, shortTerm, salaryMax, mvMin, mvMax, country, position, ageMin, ageMax, debouncedQ],
  );
  const { listings, loading } = useLastMinuteFeed(filters);
  // Sender states on the agent feed (Invariant 9): pending / accepted only.
  const { byListingId: sentMap, reload: reloadSent } = useSentRequestsMap(!isClub);

  const counts = useMemo(() => {
    const c = { "": listings.length, contract_free: 0, expiring_precontract: 0, for_sale: 0 };
    listings.forEach((l) => (c[l.urgency_class] = (c[l.urgency_class] || 0) + 1));
    return c;
  }, [listings]);
  const shown = useMemo(
    () => sortRows(urgency ? listings.filter((l) => l.urgency_class === urgency) : listings, sort),
    [listings, urgency, sort],
  );
  const serverFiltersActive =
    !!(salaryMax || mvMin || mvMax || country || readyToFly || shortTerm || position || ageMin || ageMax || debouncedQ.trim());
  const activeFilterCount = [salaryMax, mvMin || mvMax, country, readyToFly, shortTerm, position, ageMin || ageMax].filter(
    Boolean,
  ).length;

  const clearAll = () => {
    setSalaryMax(null);
    setSalaryCustom({ min: "", max: "" });
    setMvMin("");
    setMvMax("");
    setMvCustom({ min: "", max: "" });
    setCountry("");
    setReadyToFly(false);
    setShortTerm(false);
    setPosition("");
    setPosGroup("");
    setAgeMin("");
    setAgeMax("");
    setQ("");
  };

  // Composer (agent) opens via URL; both entry points land here (Invariant 13).
  const composeParam = searchParams.get("compose");
  const prefillPlayerId = searchParams.get("playerId");
  const composerOpen = composeParam === "1" || composeParam === "player";
  const openComposer = () => setSearchParams({ compose: "1" });
  const closeComposer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("compose");
    next.delete("playerId");
    setSearchParams(next);
  };

  const [opportunityFor, setOpportunityFor] = useState(null);
  const [quickView, setQuickView] = useState(null);

  // Deep-link from the in-chat card: ?listing=<id> opens that listing's quick view
  // (fetched via the feed retrieve, so anonymity is enforced for the viewer).
  const listingParam = searchParams.get("listing");
  useEffect(() => {
    if (!listingParam) return undefined;
    let active = true;
    lastMinuteService.getListing(listingParam).then((res) => {
      if (active && res.success) setQuickView(res.data);
    });
    return () => {
      active = false;
    };
  }, [listingParam]);
  const closeQuickView = () => {
    setQuickView(null);
    if (searchParams.get("listing")) {
      const next = new URLSearchParams(searchParams);
      next.delete("listing");
      setSearchParams(next);
    }
  };
  const [shareFor, setShareFor] = useState(null);
  const [contacting, setContacting] = useState(null);

  // Card-face Add-to-Radar (D1): existing radar endpoint, same gates as the
  // in-chat card (club viewer + identity available). The BE add is idempotent.
  const [radarIds, setRadarIds] = useState(() => new Set());

  // T027: seed the "On radar" state from the feed's per-listing `on_radar` flag, which
  // the BE computes for the VIEWING club from its ACTIVE Radar-V2 entries. The state
  // used to live only here, so a reload showed "Add to radar" again for a player who
  // WAS pinned. Merging into the previous set (rather than replacing it) keeps the
  // optimistic update below alive across the refetch a filter change triggers.
  useEffect(() => {
    const pinned = listings.filter((l) => l.on_radar).map((l) => l.id);
    if (!pinned.length) return;
    setRadarIds((prev) => {
      if (pinned.every((id) => prev.has(id))) return prev; // no-op → no re-render
      const next = new Set(prev);
      pinned.forEach((id) => next.add(id));
      return next;
    });
  }, [listings]);

  const addToRadarFromCard = async (listing) => {
    const res = await lastMinuteService.addToRadar(listing.id);
    if (res.success) {
      setRadarIds((prev) => new Set(prev).add(listing.id));
    } else {
      toast.error(res.error || "Could not add to radar.");
    }
  };

  // Club → listing agent: open the standard chat (club_agent 5d timer starts on the BE).
  const contact = async (listing) => {
    if (listing.blocked) return;
    setContacting(listing.id);
    const res = await lastMinuteService.contactAgent(listing.id);
    setContacting(null);
    if (res.success && res.data?.channel_id) {
      navigate(`/inbox?channel=${res.data.channel_id}`);
    } else {
      toast.error(res.error || "Could not open the chat.");
    }
  };

  // Card primary action, per role + blocked state — artboard button tokens
  // (radius 10 / mobile 11, 13px/600; blocked = #EEF2F7 on #9AA8BC).
  const btnBase = "w-full py-3 sm:py-[9px] rounded-[11px] sm:rounded-[10px] text-[13px] font-semibold text-center";
  const cardPrimary = (l) => {
    if (isClub) {
      if (l.blocked) {
        return (
          <button disabled className={`${btnBase} bg-[#EEF2F7] text-[#9AA8BC] cursor-not-allowed`}>
            Contact agent
          </button>
        );
      }
      // A revealed listing (anonymized && named-to-me) implies the club already
      // contacted the agent — a chat exists — so the primary is "Open chat" (design
      // 2a/9c). The contact endpoint is idempotent (returns the same direct
      // channel), so it doubles as "open the existing chat".
      const revealed = !!l.anonymized && !!l.is_named;
      return (
        <button
          onClick={() => contact(l)}
          disabled={contacting === l.id}
          className={`${btnBase} bg-[#2283FF] text-white hover:bg-[#1B6FE0] disabled:opacity-60`}
        >
          {contacting === l.id ? "Opening…" : revealed ? "Open chat" : "Contact agent"}
        </button>
      );
    }
    // Agent colleague view. Only two sender states ever surface (Invariant 9): a
    // pending (or silently-ignored) request → disabled "Request pending" (9c:
    // dashed border, ring-clock glyph); an accepted request → "Open chat". No
    // rejection state exists.
    const sent = sentMap.get(l.id);
    if (sent?.status === "accepted") {
      return (
        <button
          onClick={() => navigate(`/inbox?channel=${sent.channel_cid}`)}
          className={`${btnBase} bg-[#2283FF] text-white hover:bg-[#1B6FE0]`}
        >
          Open chat
        </button>
      );
    }
    if (sent) {
      return (
        <button
          disabled
          className={`${btnBase} border border-dashed border-[#C7D3E2] bg-[#F4F6FA] text-[#71829A] cursor-default flex items-center justify-center gap-[7px]`}
        >
          <PendingClockGlyph /> Request pending
        </button>
      );
    }
    return (
      <button onClick={() => setOpportunityFor(l)} className={`${btnBase} bg-[#2283FF] text-white hover:bg-[#1B6FE0]`}>
        I have an opportunity
      </button>
    );
  };

  const subtitle = isClub
    ? "Players and staff available right now, listed directly by their agents."
    : "Colleagues' listings shared with agents. Bring them an opportunity; every route goes to the listing agent.";

  const activeGroupMeta = POSITION_GROUPS.find((g) => g.key === (posGroup || groupForPosition(position)));
  const mvPresetActive = (p) => String(mvMin || "") === String(p.min ?? "") && String(mvMax || "") === String(p.max ?? "");
  const salaryTokenLabel = salaryMax
    ? SALARY_PRESETS.find((p) => p.max === salaryMax && !salaryCustom.max)?.label ||
      bandChipLabel(parseMoneyShorthand(salaryCustom.min), salaryMax) ||
      `Up to €${Math.round(salaryMax / 1000)}k`
    : "";
  const mvTokenLabel = mvMin || mvMax ? bandChipLabel(mvMin ? Number(mvMin) : null, mvMax ? Number(mvMax) : null, { prefix: "MV " }) : "";

  // Filters chip (mobile trigger — 8a).
  const mobileFiltersChip = (
    <button
      onClick={() => setFilterSheetOpen(true)}
      className="sm:hidden inline-flex items-center gap-1.5 rounded-full bg-white border border-[#DFE6EF] px-3 py-1.5 text-[12px] font-semibold text-[#33475E] flex-shrink-0"
    >
      Filters
      {activeFilterCount > 0 && (
        <span className="inline-flex items-center justify-center min-w-[16px] h-4 rounded-full bg-[#2283FF] text-white text-[10px] font-bold px-1">
          {activeFilterCount}
        </span>
      )}
    </button>
  );

  return (
    <div className="mx-auto px-4 sm:px-9 pt-5 sm:pt-[26px] pb-11">
      {/* Breadcrumb */}
      <p className="text-[12px] font-medium text-[#8A98AC] mb-2">
        Marketplace&nbsp;&nbsp;/&nbsp;&nbsp;<span className="text-[#5A6B82]">Last Minute</span>
      </p>

      {/* Title row */}
      <div className="flex items-center gap-2.5">
        <h1 className="text-[22px] sm:text-[27px] font-bold tracking-[-0.01em] text-[#14263D]">Last Minute</h1>
        {/* 7c/8k: LM info-dot optics (serif ring, open = solid blue + filled Got it) */}
        <ScoutInfoDot
          id="last-minute"
          variant="lm"
          title="What is Last Minute?"
          body="Players and staff who can move right now, listed directly by their agents: contract-free, expiring, or for sale. Contact always goes to the listing agent, and the player's current club never sees what's posted here. Some profiles stay anonymous until there's real interest."
        />
        <span className="ml-auto flex items-center gap-2">
          {isClub && isNarrow && mobileFiltersChip}
          {!isClub && (
            <button
              onClick={openComposer}
              className="inline-flex items-center rounded-[10px] bg-[#2283FF] text-white text-[13px] sm:text-[13.5px] font-semibold px-3.5 sm:px-[18px] py-2 sm:py-2.5 shadow-[0_2px_6px_rgba(34,131,255,.35)] hover:bg-[#1B6FE0] flex-shrink-0 whitespace-nowrap"
            >
              +&nbsp;&nbsp;List a player
            </button>
          )}
        </span>
      </div>
      <p className="text-[13.5px] text-[#5A6B82] mt-[5px]">{subtitle}</p>

      {/* Level segmented control · divider · Players/Staff underline tabs · search */}
      <div className="flex flex-wrap items-center gap-x-[22px] gap-y-3 mt-[18px] sm:mt-[22px]">
        <div className="flex bg-[#EAEFF5] rounded-[11px] p-[3px] w-full sm:w-auto">
          {[
            { value: "professional", label: "Professional" },
            { value: "youth", label: "Youth" },
          ].map((l) => (
            <button
              key={l.value}
              onClick={() => setLevel(l.value)}
              className={`flex-1 sm:flex-none text-center px-4 sm:px-[18px] py-[7px] rounded-lg text-[12.5px] sm:text-[13px] ${
                level === l.value
                  ? "bg-white font-semibold text-[#14263D] shadow-[0_1px_2px_rgba(16,24,40,.08)]"
                  : "font-medium text-[#5A6B82]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="hidden sm:block w-px h-6 bg-[#DFE6EF]" />
        <div className="flex items-center gap-5 self-stretch">
          {[
            { value: "player", label: "Players" },
            { value: "staff", label: "Staff" },
          ].map((t) => {
            const active = listingType === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setListingType(t.value)}
                className={`relative flex items-center px-0.5 pb-2 text-[13px] sm:text-[13.5px] ${
                  active ? "font-semibold text-[#14263D]" : "font-medium text-[#5A6B82]"
                }`}
              >
                {t.label}
                {active && (
                  <span className="text-[#8A98AC] font-semibold">&nbsp;{loading ? "" : counts[""]}</span>
                )}
                {active && <span className="absolute left-0 right-0 bottom-0 h-[2.5px] bg-[#2283FF] rounded-[2px]" />}
              </button>
            );
          })}
          {!isClub && isNarrow && <span className="ml-auto">{mobileFiltersChip}</span>}
        </div>
        {/* Search — 250px right slot (1a); focused ring per 1b. Own full-width row
            on mobile (V2 grammar; the 8a board keeps filters behind the sheet). */}
        <div
          className={`flex items-center gap-2 bg-white rounded-[10px] px-3 py-2 w-full sm:w-[250px] sm:ml-auto ${
            searchFocus
              ? "border-[1.5px] border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,.12)]"
              : "border border-[#DFE6EF]"
          }`}
        >
          <MagnifierGlyph size={12} color={searchFocus ? "#5A6B82" : "#93A5BC"} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setSearchFocus(true)}
            onBlur={() => setSearchFocus(false)}
            placeholder="Search listings"
            className="flex-1 min-w-0 text-[12.5px] text-[#14263D] font-medium placeholder-[#8A98AC] focus:outline-none bg-transparent"
          />
        </div>
      </div>

      {/* Urgency class pills — nowrap scroller on mobile (8a), single row desktop */}
      <div className="flex flex-nowrap sm:flex-wrap items-center gap-2 mt-[18px] overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0">
        {URGENCY_TABS.map((t) => {
          const active = urgency === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setUrgency(t.value)}
              className={`inline-flex items-center gap-[7px] rounded-full text-[12.5px] font-semibold flex-shrink-0 whitespace-nowrap ${
                active
                  ? "bg-[#17293F] text-white px-3.5 py-1.5"
                  : "bg-white border border-[#DFE6EF] text-[#4A5C72] px-[13px] py-1.5"
              }`}
            >
              {t.dot && <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: t.dot }} />}
              {t.label}{" "}
              <span className={active ? "opacity-[.65]" : "text-[#98A5B8]"}>{loading ? "" : counts[t.value] ?? 0}</span>
            </button>
          );
        })}
        <span className="ml-auto flex-shrink-0 hidden sm:block">
          {isClub ? (
            <span className="relative">
              <button
                type="button"
                onClick={() => setSortOpen((v) => !v)}
                className="text-[12.5px] font-semibold text-[#5A6B82] whitespace-nowrap"
              >
                Sort · {SORT_OPTIONS.find((o) => o.key === sort)?.label}{" "}
                <span className="text-[9px] text-[#8A98AC]" aria-hidden="true">▼</span>
              </button>
              {sortOpen && (
                <>
                  <span className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} />
                  <span className="absolute right-0 z-20 mt-1.5 w-[150px] bg-white border border-[#D8DFE9] rounded-[9px] shadow-[0_16px_40px_-16px_rgba(16,24,40,0.3)] py-1 flex flex-col">
                    {SORT_OPTIONS.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => {
                          setSort(o.key);
                          setSortOpen(false);
                        }}
                        className={`text-left px-3 py-2 text-[12.5px] font-medium truncate ${
                          sort === o.key ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : "text-[#33475E] hover:bg-gray-50"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </span>
                </>
              )}
            </span>
          ) : (
            <span className="text-[12px] text-[#98A5B8]">Showing listings visible to agents</span>
          )}
        </span>
      </div>

      {/* Filter chips row (desktop — mobile collapses into the 8b sheet) */}
      <div className="hidden sm:flex flex-wrap items-center gap-2 mt-3">
        {listingType === "player" && (
          <FilterChip
            label="Position"
            activeLabel={position ? `Position: ${codeForPosition(position)}` : ""}
            onClear={() => {
              setPosition("");
              setPosGroup("");
            }}
            panelWidth={340}
          >
            {/* 9e: group for speed, detail when it matters */}
            <div className="flex gap-[7px] flex-wrap">
              {POSITION_GROUPS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => {
                    if ((posGroup || groupForPosition(position)) === g.key) {
                      setPosGroup("");
                      setPosition("");
                    } else {
                      setPosGroup(g.key);
                      if (g.details.length === 1) setPosition(g.details[0].be);
                      else if (!g.details.some((d) => d.be === position)) setPosition("");
                    }
                  }}
                  className={pillCls((posGroup || groupForPosition(position)) === g.key, "px-3.5 py-[7px] text-[12.5px]")}
                >
                  {g.label}
                </button>
              ))}
            </div>
            {activeGroupMeta && activeGroupMeta.details.length > 1 && (
              <div className="flex flex-wrap gap-[7px] mt-[9px] px-3 py-2.5 bg-[#F8FAFC] border border-[#EEF1F6] rounded-xl">
                {activeGroupMeta.details.map((d) => (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => setPosition(position === d.be ? "" : d.be)}
                    className={pillCls(position === d.be, "px-3 py-1.5 text-[12px]")}
                  >
                    {d.code}
                  </button>
                ))}
              </div>
            )}
          </FilterChip>
        )}
        <FilterChip
          label="Age"
          activeLabel={ageMin || ageMax ? `Age: ${ageMin || "…"}-${ageMax || "…"}` : ""}
          onClear={() => {
            setAgeMin("");
            setAgeMax("");
          }}
          panelWidth={240}
        >
          <p className="text-[11.5px] text-[#8A98AC] mb-2">Listings without an age drop out when a bound is set.</p>
          <div className="flex items-center gap-2">
            <input
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
              placeholder="Min"
              inputMode="numeric"
              className="w-full border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
            />
            <span className="text-[#8A98AC]">–</span>
            <input
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
              placeholder="Max"
              inputMode="numeric"
              className="w-full border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
            />
          </div>
        </FilterChip>
        {listingType === "player" && (
          <FilterChip
            label="Market value"
            activeLabel={mvTokenLabel}
            onClear={() => {
              setMvMin("");
              setMvMax("");
              setMvCustom({ min: "", max: "" });
            }}
            panelWidth={360}
          >
            {/* 9e: replaces league levels, one comparable number */}
            <div className="flex flex-wrap gap-[7px]">
              {MV_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setMvCustom({ min: "", max: "" });
                    if (mvPresetActive(p)) {
                      setMvMin("");
                      setMvMax("");
                    } else {
                      setMvMin(p.min == null ? "" : String(p.min));
                      setMvMax(p.max == null ? "" : String(p.max));
                    }
                  }}
                  className={pillCls(mvPresetActive(p), "px-[13px] py-[7px] text-[12px]")}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-[9px] border border-[#DFE6EF] rounded-xl px-3 py-2.5">
              <span className="text-[12px] font-semibold text-[#8A98AC]">Custom</span>
              <input
                value={mvCustom.min}
                onChange={(e) => {
                  const v = e.target.value;
                  setMvCustom((c) => ({ ...c, min: v }));
                  const parsed = parseMoneyShorthand(v);
                  setMvMin(parsed == null ? "" : String(parsed));
                }}
                placeholder="1M"
                className="w-[70px] border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
              />
              <span className="text-[12px] text-[#8A98AC]">–</span>
              <input
                value={mvCustom.max}
                onChange={(e) => {
                  const v = e.target.value;
                  setMvCustom((c) => ({ ...c, max: v }));
                  const parsed = parseMoneyShorthand(v);
                  setMvMax(parsed == null ? "" : String(parsed));
                }}
                placeholder="8M"
                className="w-[70px] border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
              />
              {mvTokenLabel && (
                <span className="ml-auto rounded-full bg-[#F0F7FF] border border-[#CFE3FB] text-[#1466D8] text-[11.5px] font-semibold px-2.5 py-1 whitespace-nowrap">
                  {mvTokenLabel}
                </span>
              )}
            </div>
            <p className="text-[11px] text-[#98A5B8] mt-1.5">
              Shorthand works: type “750k”. Named cards show the exact value; anonymized cards show a range.
            </p>
          </FilterChip>
        )}
        <FilterChip
          label="Salary band"
          activeLabel={salaryTokenLabel ? `Salary: ${salaryTokenLabel}` : ""}
          onClear={() => {
            setSalaryMax(null);
            setSalaryCustom({ min: "", max: "" });
          }}
          panelWidth={360}
        >
          {/* 9e: presets or your own range, always displayed as a band */}
          <div className="flex flex-wrap gap-[7px]">
            {SALARY_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setSalaryCustom({ min: "", max: "" });
                  setSalaryMax(salaryMax === p.max && !salaryCustom.max ? null : p.max);
                }}
                className={pillCls(salaryMax != null && salaryMax === p.max && !salaryCustom.max, "px-[13px] py-[7px] text-[12px]")}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-[9px] border border-[#DFE6EF] rounded-xl px-3 py-2.5">
            <span className="text-[12px] font-semibold text-[#8A98AC]">Custom</span>
            <input
              value={salaryCustom.min}
              onChange={(e) => setSalaryCustom((c) => ({ ...c, min: e.target.value }))}
              placeholder="25k"
              className="w-[70px] border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
            />
            <span className="text-[12px] text-[#8A98AC]">–</span>
            <input
              value={salaryCustom.max}
              onChange={(e) => {
                const v = e.target.value;
                setSalaryCustom((c) => ({ ...c, max: v }));
                setSalaryMax(parseMoneyShorthand(v) || null);
              }}
              placeholder="45k"
              className="w-[70px] border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg py-1.5 text-[12.5px] font-semibold text-[#14263D] text-center"
            />
            <span className="text-[12px] font-semibold text-[#4A5C72] whitespace-nowrap">EUR / mo</span>
          </div>
          <p className="text-[11px] text-[#98A5B8] mt-1.5">
            Shorthand works: type “750k”. Exact figures never appear anywhere; values always render as a band.
          </p>
        </FilterChip>
        <FilterChip
          label="Country"
          activeLabel={country ? `Country: ${country}` : ""}
          onClear={() => setCountry("")}
          panelWidth={280}
        >
          {/* Not designed in the handoff → platform dropdown constrained per V2
              tokens: search + capped scrolling list, single-line truncated rows. */}
          <input
            value={countryQuery}
            onChange={(e) => setCountryQuery(e.target.value)}
            placeholder="Search countries…"
            className="w-full border border-[#E4E9F1] bg-[#F8FAFC] rounded-lg px-2.5 py-1.5 text-[12.5px] mb-2"
          />
          <div className="max-h-[200px] overflow-y-auto flex flex-col">
            {countries
              .filter((c) => (countryQuery ? (c.name || "").toLowerCase().includes(countryQuery.toLowerCase()) : true))
              .slice(0, 40)
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCountry(country === c.name ? "" : c.name)}
                  className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md text-[12.5px] font-medium ${
                    country === c.name ? "bg-[#F0F7FF] text-[#1466D8] font-semibold" : "text-[#33475E] hover:bg-gray-50"
                  }`}
                >
                  {c.image_url && <img src={c.image_url} alt="" className="w-4 h-3 object-contain flex-none" referrerPolicy="no-referrer" />}
                  <span className="truncate">{c.name}</span>
                </button>
              ))}
          </div>
        </FilterChip>
        {/* Switch toggles (1a) */}
        <span className="inline-flex items-center gap-2 ml-2.5 text-[12.5px] font-semibold text-[#5A6B82]">
          Ready to Fly
          <Switch on={readyToFly} onToggle={() => setReadyToFly((v) => !v)} label="Ready to Fly" />
        </span>
        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#5A6B82]">
          Short-term
          <Switch on={shortTerm} onToggle={() => setShortTerm((v) => !v)} label="Short-term" />
        </span>
        <span className="ml-auto text-[12px] whitespace-nowrap">
          {serverFiltersActive || urgency ? (
            <span className="text-[#5A6B82] font-medium">
              {urgency && shown.length !== listings.length
                ? `${shown.length} of ${listings.length} match`
                : `${shown.length} match`}
              &nbsp;·&nbsp;
              <button type="button" onClick={clearAll} className="text-[#1466D8] font-semibold">
                Clear all
              </button>
            </span>
          ) : (
            <span className="text-[#98A5B8]">
              {shown.length} listing{shown.length === 1 ? "" : "s"}
            </span>
          )}
        </span>
      </div>

      {/* Grid / empty / skeleton — V2 column ladder capped 1800/2160 */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 gap-4 md:gap-[18px] w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto mt-5">
          {[0, 1, 2].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="mt-9 bg-white border border-[#E4E9F1] rounded-2xl px-10 py-[52px] flex flex-col items-center gap-2.5 text-center">
          <div className="w-[52px] h-[52px] rounded-full bg-[#EEF3FA] flex items-center justify-center mb-1.5">
            <EmptyClockGlyph />
          </div>
          <p className="text-[16.5px] font-bold text-[#14263D]">
            No {level === "youth" ? "youth" : "professional"} {listingType === "staff" ? "staff" : "players"} listed right now
          </p>
          <p className="text-[13px] text-[#5A6B82] leading-[1.55] max-w-[380px]">
            New listings appear here the moment an agent posts one. Check back, or widen your view.
          </p>
          {level === "youth" && (
            <button
              onClick={() => setLevel("professional")}
              className="mt-2.5 px-[18px] py-[9px] rounded-[10px] bg-white border border-[#D8E0EA] text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
            >
              View professional {listingType === "staff" ? "staff" : "players"}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 min-[2560px]:grid-cols-5 gap-4 md:gap-[18px] w-full min-[1920px]:max-w-[1800px] min-[2560px]:max-w-[2160px] mx-auto mt-5">
          {shown.map((l) => {
            return (
              <ListingCard
                key={l.id}
                listing={l}
                viewerClubName={user?.club_name}
                viewerCountry={user?.country}
                primary={cardPrimary(l)}
                onQuickView={setQuickView}
                // Share is on every card incl. agent side (design 3a R2, D-012).
                // Card-face Add-to-Radar (D1, Marin 2026-07-22, completed after
                // BE #169): the endpoint now covers every case — club NAMED via
                // the legacy scout-radar path, club ANONYMIZED via listing-ref
                // entry, agents via their personal radar (player-keyed or
                // listing-ref). Identical action placement for every user per
                // design 3a; the BE 403s any other role.
                onShare={setShareFor}
                onAddToRadar={addToRadarFromCard}
                radarActive={radarIds.has(l.id)}
              />
            );
          })}
        </div>
      )}

      {/* Modals */}
      <ListingComposer
        open={composerOpen}
        onClose={closeComposer}
        prefillPlayerId={composeParam === "player" ? prefillPlayerId : null}
        onPublished={() => {}}
      />
      <OpportunityComposer
        open={!!opportunityFor}
        listing={opportunityFor}
        onClose={() => {
          setOpportunityFor(null);
          reloadSent();
        }}
      />
      <LmQuickViewModal
        isOpen={!!quickView}
        listing={quickView}
        onClose={closeQuickView}
        primaryAction={quickView ? cardPrimary(quickView) : null}
        onShare={setShareFor}
      />
      <ShareActionSheet
        isOpen={!!shareFor}
        onClose={() => setShareFor(null)}
        title="Share listing"
        shareType="last_minute_listing"
        shareData={shareFor || {}}
      />
      <LmFilterSheet
        open={isNarrow && filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        count={shown.length}
        listingType={listingType}
        mvMin={mvMin}
        setMvMin={setMvMin}
        mvMax={mvMax}
        setMvMax={setMvMax}
        salaryMax={salaryMax}
        setSalaryMax={setSalaryMax}
        readyToFly={readyToFly}
        setReadyToFly={setReadyToFly}
        shortTerm={shortTerm}
        setShortTerm={setShortTerm}
        country={country}
        setCountry={setCountry}
        countries={countries}
        position={position}
        setPosition={setPosition}
        ageMin={ageMin}
        setAgeMin={setAgeMin}
        ageMax={ageMax}
        setAgeMax={setAgeMax}
      />
    </div>
  );
};

export default LastMinutePage;
